// Pure rules: given a player's in-game state, can they take a given action?
//
// One place both the client (to enable/disable the turn action bar) and the
// server (to validate) can ask "is this action available?". Costs are read from
// the config tables (the single source of truth), so this never hard-codes a
// number a rule already owns.
import { FORTRESS_CARDS_CONFIG } from "../config/fortress.js";
import { canAffordWith, substitutionsFor } from "../effects/payment.js";
/** True if `bank` covers every component of `cost` under the player's
 * payment substitutions (Conversion Treadmill / Transmutation Bench) — the
 * same `canAffordWith` the server validates with, so client and server agree. */
function affords(bank, cost, state, effectOf) {
    return canAffordWith(bank, cost, substitutionsFor(state.fortressCards, effectOf));
}
/**
 * Compute whether `action` (a core action) is available to a player in `state`.
 *
 * Only `cycle_market` is gated for now — you can cycle a Fortress out of a market
 * row only if you can afford at least one row's cycle cost (1 Food today). The
 * other core actions default to allowed until their own rules are added.
 */
export function canPerformAction(action, state, config = {}) {
    switch (action) {
        case "cycle_market": {
            const fc = config.fortressCards ?? FORTRESS_CARDS_CONFIG;
            const effectOf = config.effectOf ?? (() => undefined);
            // Available if the player can pay for cycling in either market row.
            const affordable = affords(state.bank, fc.cycleCostPerRegularCard, state, effectOf) ||
                affords(state.bank, fc.cycleCostPerWinCard, state, effectOf);
            return affordable
                ? { allowed: true }
                : { allowed: false, reason: "Need at least 1 Food to cycle a card" };
        }
        // Not yet gated — each gains its own rule as the action flows are built.
        case "construct_building":
        case "construct_bridge":
        case "implement_fortress":
        case "aim_barracks":
            return { allowed: true };
        default:
            // Exhaustiveness guard: adding an ActionKind without a case fails to compile.
            return assertNever(action);
    }
}
function assertNever(value) {
    throw new Error(`canPerformAction: unhandled action kind "${String(value)}"`);
}
/** Normalize a `cycle_market` action's picks into per-row index lists — the one
 * place both wire shapes are understood. Accepts the legacy single-row shape
 * (`kind`+`indices`) alongside the per-row fields and merges them, deduped.
 * Neither range-checked nor single-row-checked here — the server validates
 * against the live rows and rejects a batch spanning both. */
export function cycleMarketRows(action) {
    const row = (kind, own) => [
        ...new Set([
            ...(own ?? []),
            ...(action.kind === kind ? (action.indices ?? []) : []),
        ]),
    ];
    return {
        regular: row("REGULAR", action.regular),
        win: row("win", action.win),
    };
}
/** Trailing field of a `pendingSteals` entry that pins the choice to the
 * VICTIM whatever passives are in play (Space Cheese Assassin's cycle theft:
 * "they choose which ones they give up"). */
export const STEAL_VICTIM_PICKS = "v";
/** Format one `pendingSteals` entry. */
export function stealEntry(attackerId, victimId, count, victimPicks = false) {
    const tail = victimPicks ? `|${STEAL_VICTIM_PICKS}` : "";
    return `${attackerId}|${victimId}|${count}${tail}`;
}
/** Parse one `pendingSteals` entry — the one place its shape is understood.
 * Entries persisted before the victim-picks flag existed simply lack it. */
export function parseStealEntry(entry) {
    const [attackerId, victimId, count, flag] = entry.split("|");
    return {
        attackerId: attackerId ?? "",
        victimId: victimId ?? "",
        count: Number(count) || 0,
        victimPicks: flag === STEAL_VICTIM_PICKS,
    };
}
/**
 * The identity an action counts as when a card asks whether two actions taken
 * in a turn were "identical" (Rinsed Repeat Offender). Two actions repeat when
 * their keys are equal, following the card's printed refinements:
 *
 * - Construct Building actions match only when the SAME building type is
 *   placed ("only identical if they are constructing the same type of
 *   building"); the target node never matters.
 * - Barracks aims match only within one posture — two attack taps, or two
 *   defend taps, never one of each.
 * - Fortress-effect (special) actions match only when the same card's effect
 *   is used twice, so the key carries the card id (and the granted action
 *   kind, for a card granting more than one).
 * - Everything else is keyed by its action type alone.
 */
export function actionIdentityKey(action) {
    switch (action.type) {
        case "construct_building":
            return `construct_building:${action.building}`;
        case "aim_barracks":
            return `aim_barracks:${action.mode}`;
        case "construct_bridge":
        case "implement_fortress":
        case "cycle_market":
            return action.type;
        default:
            return `${action.type}:${action.cardId}`;
    }
}
//# sourceMappingURL=actions.js.map